import { Router } from "@frui.ts/screens";
import { Modules } from "config";
import type { interfaces } from "inversify";
import EnumRepository from "repositories/enumRepository";
import EventBus from "services/eventBus";
import LocalizationService from "services/localizationService";
import NotificationService from "services/notificationService";
import SecurityService from "services/securityService";
import type Region from "entities/region";
import { RegionValidationRules } from "entities/region";
import { attachAutomaticValidator } from "utils/validatorAggregator";
import EditableDetailViewModel from "viewModels/editableDetailViewModel";
import { observable, reaction } from "mobx";
import { bound } from "@frui.ts/helpers";
import { ManualEntityValidator } from "@frui.ts/validation";
import RegionsViewModel from "./regionsViewModel";

@Router.registerRoute({ name: Router.Self, route: ":regionId" })
export default class RegionDetailViewModel extends EditableDetailViewModel<Region> {
  readonly modelName = "region";
  private unsubscriptions: Array<() => void> = [];
  @observable private alreadyUsed: string[] = [];
  private manualValidator = new ManualEntityValidator<Region>(true);

  constructor(
    protected itemId: string,
    protected repository: EnumRepository,
    protected localizationService: LocalizationService,
    protected eventBus: EventBus,
    protected notificationService: NotificationService,
    protected securityService: SecurityService,
    protected regionsViewModel: RegionsViewModel
  ) {
    super(itemId);
  }

  async onInitialize(): Promise<void> {
    await super.onInitialize();
    this.unsubscriptions.push(reaction(() => this.item.licensePlateLetter, this.onLicensePlateLetterChange));
  }

  protected async loadDetail(): Promise<Region> {
    await this.regionsViewModel.loadData();
    const item = await this.repository.get<Region>(this.itemId, "regions");
    this.alreadyUsed = this.regionsViewModel.items
      .map(region => {
        return region.licensePlateLetter;
      })
      .filter(licensePlateLetter => licensePlateLetter !== item.licensePlateLetter);

    attachAutomaticValidator(item, {
      ...RegionValidationRules,
      licensePlateLetter: { ...RegionValidationRules.licensePlateLetter, manualValidation: this.manualValidator },
    });

    return item;
  }

  @bound
  private onLicensePlateLetterChange() {
    if (this.alreadyUsed.includes(this.item.licensePlateLetter)) {
      this.addAlreadyExistLicensePlateLetterError();
    } else {
      this.manualValidator.removeError("licensePlateLetter");
    }
  }

  private addAlreadyExistLicensePlateLetterError() {
    this.manualValidator.addError("licensePlateLetter", this.translate("licensePlateLetterMustBeUnique"));
  }

  updateData(): Promise<Region> {
    return this.repository.update(this.item, "regions");
  }

  translate(code: string, size?: number): string {
    return this.localizationService.translateModule(Modules.timeCharges, `regions.${code}`, size);
  }

  static Factory({ container }: interfaces.Context) {
    return (itemId: string) =>
      new RegionDetailViewModel(
        itemId,
        container.get(EnumRepository),
        container.get(LocalizationService),
        container.get(EventBus),
        container.get(NotificationService),
        container.get(SecurityService),
        container.get(RegionsViewModel)
      );
  }
}
